/* eslint-disable jsx-a11y/no-autofocus */
import { useStore } from '@nanostores/react'
import { CheckIcon } from 'lucide-react'
import type { FormEvent, PropsWithChildren } from 'react'
import { useState } from 'react'
import { useShallow } from 'zustand/shallow'

import { Button } from '@/components/atoms/Button'
import { Checkbox } from '@/components/atoms/Checkbox'
import { DialogTitle } from '@/components/atoms/Dialog'
import { CountrySelectRaw } from '@/components/molecules/CountrySelect'
import { useScopedI18n } from '@/locales/client'
import sendEmail from '@/services/email'
import type { CountryCode, SendEmailBody } from '@/services/schemas'
import type { Item } from '@/stores/CartStore'
import useCartStore from '@/stores/CartStore'
import useConfigDataStore from '@/stores/ConfigDataStore'
import { $country } from '@/stores/Country'
import useDialogsStore from '@/stores/DialogsStore'
import { useHubStore } from '@/stores/HubStore'
import { $language } from '@/stores/Language'

type EmailFormProps = {
  titleText: string
  buttonText: string
  isSingleHub?: boolean
  hubId?: string
}

const EmailInput = () => {
  const t = useScopedI18n('emailForm')
  return (
    <div className="rounded-full border border-rsf-grey-light px-6 pb-4 pt-[14px] text-black">
      <input
        type="email"
        name="customer_email"
        id="customer_email"
        required
        className="w-full border-none bg-transparent text-base outline-none ring-0 placeholder:text-rsf-grey"
        placeholder={t('emailPlaceholder')}
      />
    </div>
  )
}

type FormFieldProps = {
  id: string
  children: string
}

const FormField = ({ id, children }: FormFieldProps) => (
  <div className="flex flex-col items-start">
    <label className="sr-only text-lg font-semibold leading-10 text-black" htmlFor={id}>
      {children}
    </label>
    <input
      type="text"
      name={id}
      id={id}
      placeholder={children}
      className="w-full rounded-full border border-rsf-grey-light px-6 pb-4 pt-[14px] text-base text-black outline-none ring-0 placeholder:text-rsf-grey"
      required
    />
  </div>
)

type CheckboxProps = {
  checked: boolean
  onChange: (checked: boolean) => void
}

const NewsletterCheckbox = ({ checked, onChange }: CheckboxProps) => {
  const t = useScopedI18n('emailForm')
  return (
    <div className="flex items-center gap-2">
      <input type="hidden" name="subscribeNewsletter" value={String(checked)} />
      <Checkbox id="terms" checked={checked} onCheckedChange={onChange} />
      <label
        htmlFor="terms"
        className="text-sm leading-none text-rsf-grey-dark peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {t('subscribeNewsletter')}
      </label>
    </div>
  )
}

const CountrySelectField = ({ children }: PropsWithChildren & { id: string }) => {
  const [country, setCountry] = useState<CountryCode>()
  const handleChange = (value: CountryCode) => setCountry(value)

  return (
    <div className="flex flex-col items-start">
      <label className="sr-only text-lg font-semibold leading-10 text-black" htmlFor="isocode">
        {children}
      </label>
      <CountrySelectRaw
        value={country}
        handleChange={handleChange}
        className="h-auto w-full rounded-full border border-rsf-grey-light px-6 pb-4 pt-[14px] text-base text-black outline-none ring-0 placeholder:text-rsf-grey"
      />
      <input type="hidden" id="isocode" name="isocode" value={country}></input>
    </div>
  )
}

function EmailForm({ buttonText, titleText, isSingleHub, hubId }: EmailFormProps) {
  const t = useScopedI18n('emailForm')
  const cartItems = useCartStore(useShallow(store => store.items))
  const totalPerHub = useCartStore(useShallow(store => store.totalPerHub()))
  const closeDialog = useDialogsStore(store => store.close)
  const lang = useStore($language)
  const selectedCountryCode = useStore($country)
  const getAll = useConfigDataStore(store => store.getAllItems)
  const getConfigByHub = useConfigDataStore(store => store.getHubItems)

  const [newsletterCheckbox, setNewsletterCheckbox] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsDisabled(true)
    setIsLoading(true)

    const formData = new FormData(event.target as HTMLFormElement)
    const body = Object.fromEntries(formData.entries()) as unknown as SendEmailBody
    // const images = await configToImage()
    body.configImages = hubId ? getConfigByHub(hubId) : getAll()
    body.cartItems = cartItems
    body.hubConfig = totalPerHub
    body.isSingleProductConfiguration = isSingleHub ? 1 : 0
    body.lang = lang
    body.isocode = selectedCountryCode as CountryCode
    body.reciever = 'customer' // Send to self, not dealer

    if (isSingleHub && hubId) {
      const filteredItems = cartItems.filter(item => item.hubId === hubId)
      const hub = useHubStore.getState().originalHubs.find(h => h.id === hubId)!

      const hubAsItem: Omit<Item, 'categoryId'> = {
        currency: hub.pricing.currency,
        hubId: hub.id,
        partId: hub.id,
        rNumber: hub.RNumber,
        preselect: false,
        preselectAmount: 0,
        amount: 1,
        limit: 0,
        name: hub.name,
        price: Number.parseFloat(hub.pricing.retailprice),
      }
      body.cartItems = [hubAsItem].concat(filteredItems)
      body.hubConfig = {
        1: totalPerHub[hubId]!,
      }
    } else {
      // For multi-hub configurations, send hubPrices array
      // The backend template expects prices in this exact order: ['1', '3', '4'] (Lite, Pro, Pro Deluxe Kit)
      const allHubs = useHubStore.getState().originalHubs
      const hubIdOrder = ['1', '3', '4'] // Must match backend template's hub_ids order
      const hubPrices = hubIdOrder.map((hubId) => {
        const hub = allHubs.find(h => h.id === hubId)
        return hub ? Number.parseFloat(hub.pricing.retailprice) : 0
      })
      
      body.hubPrices = hubPrices
    }

    // @ts-expect-error ignore
    body.subscribeNewsletter = body.subscribeNewsletter !== 'false'

    try {
      await sendEmail(body)
      setEmailSent(true)
    }
    catch (error) {
      console.error('Failed to send email:', error)
      // TODO: Show error message to user
    }
    finally {
      setIsLoading(false)
      setIsDisabled(false)
    }
  }

  const handleClose = () => {
    closeDialog('sendMyself')
    closeDialog('configurationPreview')
  }

  if (emailSent) {
    return (
      <section className="flex w-full flex-col items-center gap-1">
        <DialogTitle className="text-center font-inter text-2xl font-bold leading-relaxed text-rsf-black">
          {t('sentTitle')}
          <CheckIcon className="ml-2 inline size-8 text-rsf-green" />
        </DialogTitle>

        <Button className="mt-4 w-fit self-center font-semibold" onClick={handleClose}>
          {t('close')}
        </Button>
      </section>
    )
  }

  return (
    <section className="flex w-full flex-col items-center gap-1 md:gap-8">
      <DialogTitle
        className="text-center font-inter text-2xl font-bold leading-loose text-rsf-black"
        autoFocus
        tabIndex={0}
      >
        {isLoading ? t('sendingTitle') : titleText}
      </DialogTitle>

      {isLoading
        ? (
            <div className="grid size-full place-items-center py-12">
              <div className="loader size-7" />
            </div>
          )
        : (
            <form className="mt-6 flex w-full flex-col gap-2 px-2 md:px-8" method="get" onSubmit={handleSubmit}>
              <EmailInput />
              <NewsletterCheckbox checked={newsletterCheckbox} onChange={setNewsletterCheckbox} />

              <div className="transition-all duration-700 ease-in-out">
                {newsletterCheckbox && (
                  <section className="space-y-2 py-2">
                    <FormField id="customer_firstname">{t('firstName')}</FormField>
                    <CountrySelectField id="isocode" />
                  </section>
                )}
              </div>

              <Button className="mt-6 w-fit self-center font-semibold md:mt-4" type="submit" disabled={isDisabled}>
                {buttonText}
              </Button>
            </form>
          )}

    </section>
  )
}

export default EmailForm
